import { useCallback, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BrandInsightsInstanceContext } from '@trinity/chat';
import { BrandInsightChatContexts, ChatContextType } from '@trinity/chat';
import { ChatContextItemsContext } from '@trinity/chat-components/components/Chat/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useDebounceValue } from '@trinity/chat-components/hooks/useDebounceValue';
import { ContextSearchContext, useHeaderContext } from './context';
import type { ContextSearchResultItem } from './interfaces';

type Props = {
  children: React.ReactNode;
};

export const ChatContextSearchContainer = (props: Props) => {
  const [autocompleteValue, setAutoCompleteValue] = useState<string>('');
  const debouncedValue = useDebounceValue(autocompleteValue, 250);
  const brandIdentifier = useActiveBrandIdentifierNullSafe();

  const apiClient = useChatApiClient();

  const { cancelPendingSearch, pendingContextType, pendingContext, pendingEverythingSearch } =
    useHeaderContext();
  const dispatch = useContext(ChatStateDispatchContext);

  useEffect(() => {
    if (pendingContextType || pendingEverythingSearch) {
      setAutoCompleteValue('');
    }
  }, [pendingContextType, pendingEverythingSearch]);

  const allContexts = useContext(ChatContextItemsContext);

  const { data: results } = useQuery({
    queryKey: [
      'get:chat/context-search',
      debouncedValue,
      brandIdentifier,
      pendingContextType,
      pendingEverythingSearch,
    ],
    queryFn: () => {
      if (debouncedValue.length < 2) return [];
      if (
        pendingContextType === ChatContextType.Agent &&
        pendingContext &&
        !BrandInsightChatContexts.includes(pendingContext.identifier) &&
        pendingContext.subjectAutocomplete?.headerAutocomplete
      ) {
        return apiClient.chatContext
          .getContextSubjectSearch({
            brandIdentifier,
            contextIdentifier: pendingContext.identifier,
            query: debouncedValue,
          })
          .then((res) => res.results);
      }

      if (!pendingContextType && pendingEverythingSearch === false) {
        return [];
      }

      return apiClient.chat
        .contextSearch({
          q: debouncedValue,
          type: pendingEverythingSearch ? ChatContextType.Global : pendingContextType,
          brandIdentifier,
          contextIdentifier: pendingContext.identifier,
        })
        .then((res) => res.data?.results);
    },
    placeholderData: (p) => p ?? ([] as ContextSearchResultItem[]),
  });

  const handleItemSelected = useCallback(
    (item: ContextSearchResultItem) => {
      if (pendingContextType === ChatContextType.Agent) {
        let agentContext: BrandInsightsInstanceContext.AgentWithMetadata;

        if (pendingContext.identifier !== item.type) {
          const newContext = allContexts.find((ctx) => ctx.identifier === item.type);

          if (!newContext) {
            console.error('No context found for type:', item.type);
            cancelPendingSearch();
            return;
          }

          agentContext = {
            data: {
              ...newContext,
              subject: {
                id: item.entityId,
                objectId: item.objectId,
                name: item.name,
                link: item.link,
              },
            },
            type: ChatContextType.Agent,
          };
        } else {
          agentContext = {
            data: {
              ...pendingContext,
              subject: {
                id: item.entityId,
                objectId: item.objectId,
                name: item.name,
                link: item.link,
              },
            },
            type: ChatContextType.Agent,
          };
        }

        dispatch({
          type: 'instance-context-changed',
          payload: {
            context: agentContext,
            brandIdentifier,
          },
        });

        cancelPendingSearch();
        return;
      }

      return apiClient.chat
        .lookupContext({
          entityId: item.entityId,
          objectId: item.objectId,
          contextType: item.type as ChatContextType,
        })
        .then((res) => {
          dispatch({
            type: 'instance-context-changed',
            payload: {
              context: res.context,
              brandIdentifier,
            },
          });
          cancelPendingSearch();
        });
    },
    [
      allContexts,
      apiClient.chat,
      brandIdentifier,
      cancelPendingSearch,
      dispatch,
      pendingContext,
      pendingContextType,
    ]
  );

  const ctxValue = {
    updateValue: setAutoCompleteValue,
    handleItemSelected,
    value: autocompleteValue,
    results,
  };

  return <ContextSearchContext.Provider value={ctxValue}>{props.children}</ContextSearchContext.Provider>;
};
